import type { ReactNode } from 'react';
import { Date } from 'components/date';
import { Field } from 'components/field';
import { LinkCell } from 'components/link-cell';
import { Section } from 'components/section';
import type { AfeHeaderDetail } from '../types';
import { formatAfeType, renderEntityLinks } from './utils';
import styles from './fields-grid.module.scss';

interface AfeDetailPanelProps {
  detail: AfeHeaderDetail;
}

const AfeDetailPanel = ({ detail }: AfeDetailPanelProps) => {
  const senders = detail.entities.filter((entity) => entity.role === 'sender');
  const receivers = detail.entities.filter(
    (entity) => entity.role === 'receiver'
  );

  const unit = detail.wellInformation.unit ?? detail.wellLookup.unit;
  const unitHref =
    detail.wellInformation.unitHref ?? detail.wellLookup.unitHref;

  const fields: Array<{ label: string; value: ReactNode }> = [
    {
      label: 'Type',
      value: formatAfeType(detail.afeTypeLabel ?? detail.afeType),
    },
    { label: 'Basin', value: detail.wellInformation.basinName },
    {
      label: 'Reported Formation',
      value: detail.wellInformation.reportedFormation,
    },
    {
      label: 'Well Name',
      value: detail.wellInformation.wellName ?? detail.wellLookup.wellName,
    },
    {
      label: 'UWI',
      value: <LinkCell value={detail.slotId} href={detail.slotHref} />,
    },
    {
      label: 'Unit',
      value: <LinkCell value={unit} href={unitHref} />,
    },
    { label: 'Well Type', value: detail.wellTypeLabel ?? detail.wellType },
    {
      label: 'Operator',
      value: (
        <LinkCell
          value={detail.operator}
          href={detail.operatorHref}
          showTitle
        />
      ),
    },
    ...(detail.currentWellOperator
      ? [
          {
            label: 'Current Well Operator',
            value: (
              <LinkCell
                value={detail.currentWellOperator}
                href={detail.currentWellOperatorHref}
                showTitle
              />
            ),
          },
        ]
      : []),
    { label: 'Sender', value: renderEntityLinks(senders) },
    { label: 'Receiver', value: renderEntityLinks(receivers) },
    { label: 'County', value: detail.wellInformation.countyName },
    {
      label: 'Created At',
      value: <Date date={detail.createdAt} format="M/DD/YYYY" />,
    },
  ];

  return (
    <Section header="AFE Details">
      <div className={styles.fieldsGrid}>
        {fields.map((field) => (
          <div className={styles.fieldItem} key={field.label}>
            <Field label={field.label} value={field.value} />
          </div>
        ))}
      </div>
    </Section>
  );
};

export { AfeDetailPanel };
